import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi } from "@/admin/AdminAuth";
import { toast } from "sonner";

const SLUG = "peptichill-peptide-cooler-fridge";

export default function AdminProducts() {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get(`/admin/products/${SLUG}`);
        setProduct({
          ...res.data,
          _priceLb: (res.data.price_pennies / 100).toFixed(2),
          _bulletText: res.data.bullet_points.join("\n"),
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setSpec = (key, value) =>
    setProduct((p) => ({ ...p, specs: { ...p.specs, [key]: value } }));

  const moveImage = (i, dir) => {
    setProduct((p) => {
      const j = i + dir;
      if (j < 0 || j >= p.images.length) return p;
      const imgs = [...p.images];
      [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
      return { ...p, images: imgs };
    });
  };
  const removeImage = (i) =>
    setProduct((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }));
  const addImage = () => {
    const url = newImageUrl.trim();
    if (!/^https?:\/\//.test(url)) {
      toast.error("Enter a valid http(s) URL.");
      return;
    }
    setProduct((p) => ({ ...p, images: [...p.images, url] }));
    setNewImageUrl("");
  };

  const save = async () => {
    if (!product) return;
    const pricePennies = Math.round(parseFloat(product._priceLb) * 100);
    if (!Number.isFinite(pricePennies) || pricePennies < 1) {
      toast.error("Enter a valid price.");
      return;
    }
    const bullets = product._bulletText.split("\n").map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      const payload = {
        name: product.name,
        price_pennies: pricePennies,
        short_description: product.short_description,
        bullet_points: bullets,
        specs: product.specs,
        images: product.images,
        stock_status: product.stock_status,
        stock_quantity: product.stock_quantity,
      };
      const res = await adminApi.patch(`/admin/products/${SLUG}`, payload);
      setProduct({
        ...res.data,
        _priceLb: (res.data.price_pennies / 100).toFixed(2),
        _bulletText: res.data.bullet_points.join("\n"),
      });
      toast.success("Product saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !product) {
    return (
      <AdminLayout title="Product">
        <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-10 flex items-center text-muted text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Product editor">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left — editable fields */}
        <div className="space-y-5">
          <Card title="Basics">
            <Field label="Name" value={product.name} onChange={(v) => setProduct({ ...product, name: v })} testId="admin-product-name" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Price (£)"
                value={product._priceLb}
                onChange={(v) => setProduct({ ...product, _priceLb: v })}
                testId="admin-product-price"
                prefix="£"
              />
              <div>
                <Label>Stock status</Label>
                <select
                  value={product.stock_status}
                  onChange={(e) => setProduct({ ...product, stock_status: e.target.value })}
                  data-testid="admin-product-stock-status"
                  className="w-full h-11 rounded-xl border border-silver/70 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
                >
                  <option value="in_stock">In stock</option>
                  <option value="low_stock">Low stock</option>
                  <option value="out_of_stock">Out of stock</option>
                </select>
              </div>
            </div>
            <Field
              label="Stock quantity"
              type="number"
              value={String(product.stock_quantity)}
              onChange={(v) => setProduct({ ...product, stock_quantity: parseInt(v || "0", 10) })}
              testId="admin-product-stock-qty"
            />
          </Card>

          <Card title="Copy">
            <div>
              <Label>Short description</Label>
              <textarea
                value={product.short_description}
                onChange={(e) => setProduct({ ...product, short_description: e.target.value })}
                rows={4}
                data-testid="admin-product-short-desc"
                className="w-full rounded-xl border border-silver/70 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
              />
            </div>
            <div>
              <Label>Bullet points (one per line)</Label>
              <textarea
                value={product._bulletText}
                onChange={(e) => setProduct({ ...product, _bulletText: e.target.value })}
                rows={5}
                data-testid="admin-product-bullets"
                className="w-full rounded-xl border border-silver/70 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan font-mono"
              />
            </div>
          </Card>

          <Card title="Specifications">
            <div className="space-y-3">
              {Object.entries(product.specs).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[1fr,2fr] gap-3 items-start">
                  <div className="text-xs uppercase tracking-widest text-muted pt-3">{k}</div>
                  <input
                    value={v}
                    onChange={(e) => setSpec(k, e.target.value)}
                    data-testid={`admin-product-spec-${k}`}
                    className="h-11 rounded-xl border border-silver/70 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right — image manager */}
        <div>
          <Card title="Images">
            <p className="text-xs text-muted mb-3">
              First image is the default. Drag with arrows to reorder.
            </p>
            <ul className="space-y-2" data-testid="admin-product-images">
              {product.images.map((url, i) => (
                <li key={url + i} className="flex items-center gap-3 rounded-xl bg-ice-light/60 border border-silver/40 p-2">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-silver/40 shrink-0">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0 text-xs text-muted font-mono truncate">{url}</div>
                  <div className="flex items-center gap-1">
                    <button type="button" disabled={i === 0} onClick={() => moveImage(i, -1)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-white disabled:opacity-30"
                      data-testid={`admin-product-image-up-${i}`}>
                      <ArrowUp size={14} />
                    </button>
                    <button type="button" disabled={i === product.images.length - 1} onClick={() => moveImage(i, 1)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-white disabled:opacity-30"
                      data-testid={`admin-product-image-down-${i}`}>
                      <ArrowDown size={14} />
                    </button>
                    <button type="button" onClick={() => removeImage(i)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-destructive/10 text-destructive"
                      data-testid={`admin-product-image-remove-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex gap-2">
              <input
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="https://…image.png"
                data-testid="admin-product-image-add-input"
                className="flex-1 h-10 rounded-xl border border-silver/70 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
              />
              <button type="button" onClick={addImage}
                data-testid="admin-product-image-add"
                className="inline-flex items-center gap-1 px-4 h-10 rounded-xl bg-navy text-white text-sm font-semibold hover:brightness-110">
                <Plus size={14} /> Add
              </button>
            </div>
          </Card>
        </div>
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-product-save"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-7 h-12 text-sm font-semibold hover:brightness-110 transition shadow-card disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </AdminLayout>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-5 space-y-4">
      <h3 className="text-sm font-bold text-navy">{title}</h3>
      {children}
    </div>
  );
}
function Label({ children }) {
  return <div className="block text-xs uppercase tracking-widest text-muted mb-2">{children}</div>;
}
function Field({ label, value, onChange, type = "text", testId, prefix }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className={`w-full h-11 rounded-xl border border-silver/70 ${prefix ? "pl-7" : "px-4"} pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-cyan`}
        />
      </div>
    </div>
  );
}
